import { Button, HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { ProgramTags } from '@/components/shared/ProgramTags';
import { getCustomerDashboardUrl } from '@/utilities/CustomerDashboardUrl';
import { ApiRetailer, ApiUser } from '@api/interfaces';
import { ApiEnergyCustomerProgram } from '@api/interfaces/ApiEnergyCustomer';
import BarChart from '@mui/icons-material/BarChart';
import { Stack } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

interface EnergyProgramStatusProps {
  customer: ApiUser,
  energy: ApiEnergyCustomerProgram,
  onViewProgress: () => void,
  retailer: ApiRetailer,
}

export const EnergyProgramStatus = ({
  customer,
  energy,
  onViewProgress,
  retailer,
}: EnergyProgramStatusProps) => {
  const { t } = useTranslation();
  const { dashboardAccessToken, isHistorical } = customer;
  const { shortUrlName } = retailer;
  const canOpenDashboard = !!dashboardAccessToken && !!shortUrlName && !isHistorical;
  const { nextTierProgress } = energy;

  return (
    <Stack data-testid="energy-program-status">
      <Stack alignItems="center" direction="row">
        <Text category="body-xlarge">{energy.programName}</Text>
        <HSpacer size="3" />
        <ProgramTags isEnergyMember testID="energy-program-status-tag" />
      </Stack>
      <Text category="body-small">
        {t(($) => $.energy.profile.programProgress, { year: energy.programYearLabel })}
      </Text>
      <VSpacer size="3" />
      {!!energy.earnedTier && (
        <Text category="body-medium" testID="energy-program-status-earned-tier">
          <Trans
            components={{ bold: <b /> }}
            i18nKey={($) => $.energy.profile.earnedTier}
            values={{ tier: energy.earnedTier.name }}
          />
        </Text>
      )}
      <Text category="body-medium" testID="energy-program-status-next-tier">
        {nextTierProgress ? (
          <Trans
            components={{ bold: <b /> }}
            count={nextTierProgress.behaviorsRemaining}
            i18nKey={($) => $.energy.profile.behaviorsToNextTier}
            values={{ tier: nextTierProgress.nextTier.name }}
          />
        ) : t(($) => $.energy.profile.topTierReached)}
      </Text>
      {!!nextTierProgress?.missingRequiredBehaviorNames.length && (
        <Text category="body-medium" testID="energy-program-status-required">
          {t(($) => $.energy.profile.requiredForNextTier, {
            names: nextTierProgress.missingRequiredBehaviorNames.join(', '),
          })}
        </Text>
      )}
      <VSpacer size="5" />
      <Stack direction="row">
        <Button
          onClick={onViewProgress}
          size="small"
          square
          startIcon={<BarChart />}
          testID="energy-view-progress-button"
        >
          {t(($) => $.customers.rewardsProgram.viewProgress)}
        </Button>
        {canOpenDashboard && (
          <>
            <HSpacer size="4" />
            <Button
              onClick={() => {
                const url = getCustomerDashboardUrl(
                  shortUrlName,
                  dashboardAccessToken,
                  '?program=energy',
                );
                window.open(url, '_blank');
              }}
              size="small"
              square
              testID="energy-customer-dashboard-button"
              variant="outlined"
            >
              {t(($) => $.customers.rewardsProgram.customerDashboard)}
            </Button>
          </>
        )}
      </Stack>
    </Stack>
  );
};
